import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { useApp } from '../store/AppContext.js';
import { Alert } from '../components/ui.js';
import { receiveSharedItemKey, decryptBytes, fromBase64 } from '@battini/crypto';
import { shareExistingItem } from '../crypto/session.js';

interface SharedRow {
  id: string;
  item_id: string;
  from_email: string;
  permission: string;
  shared_key: string;
  ciphertext: string;
  iv: string;
  message: string | null;
}

export function SharingPage() {
  const { items, vaultSession } = useApp();
  const [received, setReceived] = useState<{ title: string; from: string; permission: string }[]>([]);
  const [shareItemId, setShareItemId] = useState('');
  const [recipient, setRecipient] = useState('');
  const [permission, setPermission] = useState<'view' | 'edit' | 'reshare'>('view');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!vaultSession?.privateKey) return;
    const res = await api.get<{ shared: SharedRow[] }>('/sharing/shared-with-me');
    const dec = await Promise.all(
      res.body.shared.map(async (r) => {
        const itemKey = await receiveSharedItemKey(vaultSession.privateKey!, r.shared_key);
        const body = await decryptBytes(itemKey, fromBase64(r.iv), fromBase64(r.ciphertext));
        const fields = JSON.parse(new TextDecoder().decode(body)) as { title?: string };
        return { title: fields.title ?? '(item)', from: r.from_email, permission: r.permission };
      }),
    );
    setReceived(dec);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vaultSession]);

  const share = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const item = items.find((i) => i.id === shareItemId);
      if (!item || !vaultSession) throw new Error('Select an item you own.');
      // Look up recipient public key.
      const lookup = await api.get<{ id: string; publicKey: string }>('/auth/users/lookup', { email: recipient.trim().toLowerCase() });
      // We need the item's server-side protected_item_key; fetch fresh item.
      const itemRes = await api.get<{ item: { protectedItemKey: string } }>(`/vault/items/${item.id}`);
      const { sharedKey } = await shareExistingItem(vaultSession, item.id, lookup.body.publicKey, itemRes.body.item.protectedItemKey);
      await api.post('/sharing/share', { itemId: item.id, recipientEmail: recipient.trim().toLowerCase(), sharedKey, permission });
      setMsg(`Shared "${item.title}" with ${recipient}.`);
      setRecipient('');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Share failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid">
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Share an item</h3>
        <p className="muted" style={{ fontSize: 13 }}>The item key is wrapped with the recipient&apos;s public key. The server stores only ciphertext.</p>
        {msg && <Alert kind={msg.startsWith('Shared') ? 'success' : 'error'}>{msg}</Alert>}
        <div className="field">
          <label>Item</label>
          <select value={shareItemId} onChange={(e) => setShareItemId(e.target.value)}>
            <option value="">Select an item…</option>
            {items.filter((i) => !i.deletedAt).map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Recipient email</label>
          <input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="they@example.com" />
        </div>
        <div className="field">
          <label>Permission</label>
          <select value={permission} onChange={(e) => setPermission(e.target.value as 'view' | 'edit' | 'reshare')}>
            <option value="view">View only</option>
            <option value="edit">Edit</option>
            <option value="reshare">Re-share</option>
          </select>
        </div>
        <button className="btn" onClick={share} disabled={busy}>Share securely</button>
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Shared with you</h3>
        {received.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>No items shared with you yet.</p> : (
          <table>
            <thead><tr><th>Title</th><th>From</th><th>Permission</th></tr></thead>
            <tbody>
              {received.map((r, i) => (
                <tr key={i}><td>{r.title}</td><td className="muted">{r.from}</td><td><span className="badge muted">{r.permission}</span></td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
